/**
 * use this hook when you need to fetch large data in chunks and aggregate them from ccp
 * this will not send any data in case any of the chunk fails
 * you can use the error state to handle the error in the component
 */

import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { defaultTo, get } from 'lodash';
import { useState } from 'react';

interface Config {
  chunkSize?: number;
  totalCount?: number | null;
  fetchType?: 'parallel' | 'sequential'; // this is required to determine whether to fetch data in parallel or sequentially, if not provided, it will default to sequential. you can check which one to use by your query pageOptions, if it has cursor, then use sequential, if it has offset, then use parallel
  paths?: {
    dataPath: string;
    cursorPath?: string;
    hasNextPagePath?: string;
  };
}

interface FetchCcpDataProps {
  cursor?: string | null;
  size?: number;
  offset?: number;
  first?: number;
}

interface UpdateVariablesProps extends FetchCcpDataProps {
  vars: any;
}

const DEFAULT_CHUNK_SIZE = 100;

export const useGetAggregateCcpList = <QueryType, VariablesType>(
  client: ApolloClient<NormalizedCacheObject>,
  query: any,
  variables: any,
  config: Config = {
    fetchType: 'sequential',
  }
) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<any>(null);

  const chunkSize = config?.chunkSize || DEFAULT_CHUNK_SIZE;
  const totalCount = config?.totalCount || 0;

  const updateVariables = (props: UpdateVariablesProps) => {
    const { vars, cursor, offset, size, first } = props;
    for (const key in vars) {
      if (typeof vars[key] === 'object' && vars[key] !== null) {
        vars[key] = updateVariables({ vars: vars[key], cursor, offset, size, first });
      } else {
        if (key === 'cursor') {
          vars[key] = cursor;
        } else if (key === 'size') {
          vars[key] = size;
        } else if (key === 'offset') {
          vars[key] = offset;
        } else if (key === 'first') {
          vars[key] = first;
        }
      }
    }
    return vars;
  };

  const fetchCcpData = (props: FetchCcpDataProps) => {
    return client.query<QueryType, VariablesType>({
      query: query,
      variables: updateVariables({ vars: variables, ...props }),
      fetchPolicy: 'no-cache',
    });
  };

  const fetchPageDataInParallel = async () => {
    try {
      const nodePromises = [];
      let remaining = totalCount;
      let first = chunkSize;

      for (let i = 0; i < Math.ceil(totalCount / chunkSize); i++) {
        remaining = remaining - chunkSize;
        nodePromises.push(fetchCcpData({ offset: i * chunkSize, first }));

        if (remaining < chunkSize) {
          first = remaining;
        }
      }

      const allSettledNodes = await Promise.allSettled(nodePromises);
      const allNodes = allSettledNodes
        .map((settledPromise) => {
          if (settledPromise.status === 'rejected') {
            throw new Error(`unexpected error: ${JSON.stringify(settledPromise.reason)}`);
          }
          const response = settledPromise.value;
          /* response.error or response.errors is used to check if the response has an error,
          ideally, this is dependent on the response structure (so we can also
          do something like config?.paths?.errorPath), but this is a common pattern
         */
          if (response.error || response.errors) {
            throw new Error(`error for response: ${JSON.stringify(response)}`);
          }
          if (config?.paths?.dataPath && response) {
            const nodes = defaultTo(get(response, config.paths.dataPath), []);
            if (nodes && nodes.length) {
              return nodes;
            }
          }
          return [];
        })
        .reduce((allNodes: any[], nodes) => allNodes.concat(nodes), []);

      return allNodes;
    } catch (err) {
      setError(err);
    }
  };

  const fetchPageDataSequentially = async () => {
    try {
      const allPageData = [];

      let cursor = null;
      let hasNextPage = true;

      if (config?.paths && config.paths?.cursorPath && config.paths?.hasNextPagePath) {
        const { dataPath, cursorPath, hasNextPagePath } = config.paths;

        while (hasNextPage && allPageData.length <= totalCount) {
          const response: any = await fetchCcpData({ cursor, size: chunkSize });
          if (response.error || response.errors) {
            throw new Error(`error for response: ${JSON.stringify(response)}`);
          }

          const data = defaultTo(get(response, dataPath), []);

          if (data && data.length) {
            allPageData.push(...data);
            cursor = defaultTo(get(response, cursorPath), null);
            hasNextPage = defaultTo(get(response, hasNextPagePath), false);

            // eslint-disable-next-line max-depth
            if (!hasNextPage) {
              break;
            }
          } else {
            break;
          }
        }
      }
      return allPageData;
    } catch (err) {
      setError(err);
    }
  };

  const getCcpData = async () => {
    try {
      setIsLoading(true);
      const response =
        config.fetchType === 'parallel' ? await fetchPageDataInParallel() : await fetchPageDataSequentially();
      setIsLoading(false);
      return response;
    } catch (err) {
      setIsLoading(false);
      setError(err);
    }
  };

  return {
    isLoading,
    error,
    getCcpData,
  };
};
